class PhlurHomeHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.phlurHeroReady === "true") return;
    this.dataset.phlurHeroReady = "true";

    this.video = this.querySelector("[data-phlur-hero-video]");
    if (!this.video) return;

    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.onPlaying = () => {
      this.classList.add("has-video-frame", "is-video-playing");
      this.dataset.phlurHeroState = "playing";
    };
    this.onPaused = () => {
      this.classList.remove("is-video-playing");
      if (
        this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        this.video.currentTime > 0.05
      ) {
        this.classList.add("has-video-frame");
        this.dataset.phlurHeroState = "paused-frame";
      } else {
        this.classList.remove("has-video-frame");
        this.dataset.phlurHeroState = "poster";
      }
    };
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.pauseAndKeepFrame();
      } else {
        this.playWhenAllowed();
      }
    };
    this.onMotionChange = () => {
      if (this.motionQuery.matches) {
        this.prepareStillFrame();
      } else {
        this.playWhenAllowed();
      }
    };

    this.video.addEventListener("playing", this.onPlaying);
    this.video.addEventListener("pause", this.onPaused);
    this.video.addEventListener("ended", this.onPaused);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.motionQuery.addEventListener?.("change", this.onMotionChange);

    this.dataset.phlurHeroState = "poster";
    this.playWhenAllowed();
  }

  disconnectedCallback() {
    if (!this.video) return;
    this.video.removeEventListener("playing", this.onPlaying);
    this.video.removeEventListener("pause", this.onPaused);
    this.video.removeEventListener("ended", this.onPaused);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.motionQuery?.removeEventListener?.("change", this.onMotionChange);
    this.video.pause();
    this.classList.remove("has-video-frame", "is-video-playing");
    delete this.dataset.phlurHeroReady;
  }

  playWhenAllowed() {
    if (!this.video || document.hidden) {
      this.pauseAndKeepFrame();
      return;
    }
    if (this.motionQuery?.matches) {
      this.prepareStillFrame();
      return;
    }

    const playPromise = this.video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => this.prepareStillFrame());
    }
  }

  prepareStillFrame() {
    if (!this.video) return;

    const reveal = () => {
      const requestedTime = Number(this.video.dataset.stillTime || 1);
      const safeTime = Number.isFinite(this.video.duration)
        ? Math.min(requestedTime, Math.max(0, this.video.duration - 0.05))
        : requestedTime;
      const finish = () => {
        this.video.pause();
        this.classList.add("has-video-frame");
        this.classList.remove("is-video-playing");
        this.dataset.phlurHeroState = "paused-frame";
      };

      if (Math.abs(this.video.currentTime - safeTime) < 0.05) {
        finish();
        return;
      }

      this.video.addEventListener("seeked", finish, { once: true });
      this.video.currentTime = safeTime;
    };

    if (this.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      reveal();
    } else {
      this.video.addEventListener("loadedmetadata", reveal, { once: true });
      this.video.load();
    }
  }

  pauseAndKeepFrame() {
    if (this.video && !this.video.paused) this.video.pause();
    this.onPaused?.();
  }
}

if (!customElements.get("phlur-home-hero")) {
  customElements.define("phlur-home-hero", PhlurHomeHero);
}
